import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Code, Copy, CheckCircle, ExternalLink, Globe, Eye, EyeOff, Shield, AlertTriangle, Save, Link, HelpCircle } from 'lucide-react';
import { generateWidgetBlogUrl, generateBlogListUrl, BlogUrlConfig } from '../lib/blogUrlHelper';

interface LatestBlog {
  id: string;
  slug: string;
  title: string;
}

interface WidgetConfigurationSectionProps {
  userId: string;
  apiKey?: string;
  widgetEnabled?: boolean;
  lastUsed?: string;
  customDomain?: string;
  useCustomDomain?: boolean;
  usernameSlug?: string;
  latestBlog?: LatestBlog | null;
  onSave: (customDomain: string, useCustomDomain: boolean) => Promise<void>;
  onToggleWidget: (enabled: boolean) => Promise<void>;
}

export function WidgetConfigurationSection({
  userId,
  apiKey,
  widgetEnabled = true,
  lastUsed,
  customDomain = '',
  useCustomDomain = false,
  usernameSlug,
  latestBlog,
  onSave,
  onToggleWidget
}: WidgetConfigurationSectionProps) {
  const location = useLocation();
  const handleidingPath = location.pathname.startsWith('/support') ? '/support/handleiding' : '/admin/handleiding';
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [editCustomDomain, setEditCustomDomain] = useState(customDomain || '');
  const [editUseCustomDomain, setEditUseCustomDomain] = useState(useCustomDomain);
  const [editWidgetEnabled, setEditWidgetEnabled] = useState(widgetEnabled);
  const [saving, setSaving] = useState(false);
  const [togglingWidget, setTogglingWidget] = useState(false);
  const [widgetType, setWidgetType] = useState<'full' | 'compact'>('full');
  const [articleLimit, setArticleLimit] = useState(6);
  const [compactLimit, setCompactLimit] = useState(3);

  const baseUrl = window.location.origin;
  const widgetScriptUrl = widgetType === 'full' ? `${baseUrl}/blog-widget.js` : `${baseUrl}/blog-widget-compact.js`;

  const embedCode = widgetType === 'full'
    ? `<!-- LeesNU Blog Widget (Volledige versie) -->
<div data-leesnu-widget
     data-api-key="${apiKey || 'jouw-api-key'}"
     data-limit="${articleLimit}"></div>
<script src="${baseUrl}/blog-widget.js"></script>`
    : `<!-- LeesNU Blog Widget (Compact versie) -->
<div data-leesnu-compact-widget
     data-api-key="${apiKey || 'jouw-api-key'}"
     data-limit="${compactLimit}"></div>
<script src="${baseUrl}/blog-widget-compact.js"></script>`;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyKey = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 16) return key;
    return `${key.substring(0, 10)}...${key.substring(key.length - 6)}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editCustomDomain.trim(), editUseCustomDomain);
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Er is een fout opgetreden bij het opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWidget = async (enabled: boolean) => {
    setTogglingWidget(true);
    try {
      await onToggleWidget(enabled);
      setEditWidgetEnabled(enabled);
    } catch (error) {
      console.error('Failed to toggle widget:', error);
      alert('Er is een fout opgetreden bij het wijzigen van de widget status');
      setEditWidgetEnabled(!enabled);
    } finally {
      setTogglingWidget(false);
    }
  };

  const hasChanges = editCustomDomain !== (customDomain || '') || editUseCustomDomain !== useCustomDomain;

  const getPreviewUrl = () => {
    if (editUseCustomDomain && editCustomDomain.trim()) {
      const domain = editCustomDomain.trim().replace(/\/$/, '');
      return `${domain}#artikel/artikel-slug`;
    }
    return 'https://lees-nu.nl/blog/username/artikel-slug';
  };

  const getFinalBlogListUrl = () => {
    const config: BlogUrlConfig = {
      useCustomDomain: editUseCustomDomain,
      customDomain: editCustomDomain.trim() || null,
      usernameSlug: usernameSlug || null,
    };
    return generateBlogListUrl(config);
  };

  const getTestArticleUrl = () => {
    if (!latestBlog) return null;

    const config: BlogUrlConfig = {
      useCustomDomain: editUseCustomDomain,
      customDomain: editCustomDomain.trim() || null,
      usernameSlug: usernameSlug || null,
    };
    return generateWidgetBlogUrl(latestBlog.slug, config);
  };

  if (!apiKey) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-amber-100 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#231F20] mb-2">
              Widget Configuratie
            </h2>
            <p className="text-sm text-gray-600">
              Er is geen API key voor deze gebruiker. Contacteer de technische beheerder.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="p-3 bg-blue-100 rounded-lg">
          <Code className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-[#231F20] mb-2">
            Widget Configuratie
          </h2>
          <p className="text-sm text-gray-600">
            Beheer de widget embed code en custom domain configuratie voor deze gebruiker
          </p>
        </div>
        <RouterLink
          to={`${handleidingPath}#widget-activeren`}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-[#231F20] hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title="Open de handleiding voor de blog-widget"
        >
          <HelpCircle className="w-4 h-4" />
          Handleiding
        </RouterLink>
        {!widgetEnabled && (
          <div className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
            Gedeactiveerd
          </div>
        )}
      </div>

      <div className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Widget Status</h3>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={editWidgetEnabled}
                onChange={(e) => handleToggleWidget(e.target.checked)}
                disabled={togglingWidget}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
            </label>
            <div>
              <span className="text-sm font-medium text-gray-700">
                Widget {editWidgetEnabled ? 'Ingeschakeld' : 'Uitgeschakeld'}
              </span>
              <p className="text-xs text-gray-500">
                {togglingWidget
                  ? 'Bezig met wijzigen...'
                  : editWidgetEnabled
                    ? 'De widget is actief en kan data ophalen via de API'
                    : 'De widget is uitgeschakeld en kan geen data ophalen'}
              </p>
            </div>
          </div>
        </div>

        {!editWidgetEnabled && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-red-900 mb-1">
                Widget Gedeactiveerd
              </h3>
              <p className="text-sm text-red-700">
                De widget is momenteel gedeactiveerd voor deze gebruiker. Schakel de widget hierboven in om deze te activeren.
              </p>
            </div>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Custom Domain Configuratie
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Domain URL
              </label>
              <input
                type="text"
                value={editCustomDomain}
                onChange={(e) => setEditCustomDomain(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Volledig URL waar de widget is geïmplementeerd (bijv. https://janbakker.nl)
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={editUseCustomDomain}
                  onChange={(e) => setEditUseCustomDomain(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B35]"></div>
              </label>
              <div>
                <span className="text-sm font-medium text-gray-700">Gebruik Custom Domain</span>
                <p className="text-xs text-gray-500">
                  Als actief, gebruiken nieuwsbrieven custom domain i.p.v. lees-nu.nl
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Link className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-blue-900 mb-1">URL Preview</p>
                  <p className="text-xs text-blue-700 font-mono break-all">
                    {getPreviewUrl()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-green-900 mb-1">Lees-Nu URL voor deze gebruiker</p>
                  <p className="text-xs text-green-700 font-mono break-all">
                    {getFinalBlogListUrl()}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Dit is de uiteindelijke URL die gebruikt wordt in nieuwsbrieven
                  </p>
                </div>
              </div>
            </div>

            {latestBlog ? (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <ExternalLink className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-orange-900 mb-1">Test Widget URL</p>
                    <p className="text-xs text-orange-700 mb-2">
                      Laatste artikel: <span className="font-medium">{latestBlog.title}</span>
                    </p>
                    <a
                      href={getTestArticleUrl() || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-medium underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {getTestArticleUrl()}
                    </a>
                    <p className="text-xs text-orange-600 mt-2">
                      Klik om het artikel te openen en de widget te testen
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-900 mb-1">Test Widget URL</p>
                    <p className="text-xs text-gray-600">
                      Deze gebruiker heeft nog geen gepubliceerde artikelen. Test URL wordt beschikbaar na het eerste artikel.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#FF6B35] text-white rounded-lg hover:bg-[#E55A2B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Opslaan...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Wijzigingen Opslaan
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Widget Type</h3>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="widgetType"
                value="full"
                checked={widgetType === 'full'}
                onChange={(e) => setWidgetType(e.target.value as 'full')}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Volledige Widget</div>
                <div className="text-sm text-gray-600">Toon overzicht + open artikelen op deze pagina (met hash routing)</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="widgetType"
                value="compact"
                checked={widgetType === 'compact'}
                onChange={(e) => setWidgetType(e.target.value as 'compact')}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Compact Widget</div>
                <div className="text-sm text-gray-600">Toon alleen overzicht (3 cards grid), link door naar hoofdblog pagina</div>
              </div>
            </label>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Widget Configuratie</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aantal artikelen
            </label>
            {widgetType === 'full' ? (
              <select
                value={articleLimit}
                onChange={(e) => setArticleLimit(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
              >
                <option value={3}>3 artikelen</option>
                <option value={6}>6 artikelen</option>
                <option value={9}>9 artikelen</option>
                <option value={12}>12 artikelen</option>
              </select>
            ) : (
              <select
                value={compactLimit}
                onChange={(e) => setCompactLimit(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none"
              >
                <option value={3}>3 artikelen (Grid: 3 kolommen)</option>
                <option value={6}>6 artikelen (Grid: 2 rijen)</option>
                <option value={9}>9 artikelen (Grid: 3 rijen)</option>
              </select>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-700">
              Widget Embed Code
            </label>
            <button
              onClick={handleCopyCode}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                copied
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {copied ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Gekopieerd!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Kopieer Code
                </>
              )}
            </button>
          </div>
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
            {embedCode}
          </pre>
          <p className="text-xs text-gray-500 mt-2">
            Kopieer deze code en verstrek deze aan de gebruiker voor implementatie op hun website
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Widget API Key
            </h3>
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 font-mono flex items-center justify-between">
                  <span>{showKey ? apiKey : maskApiKey(apiKey)}</span>
                  <button
                    onClick={() => setShowKey(!showKey)}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={handleCopyKey}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    copiedKey
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {copiedKey ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              {lastUsed && (
                <p className="text-xs text-gray-500">
                  Laatst gebruikt: {new Date(lastUsed).toLocaleString('nl-NL')}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            Widget Type Uitleg
          </h3>
          {widgetType === 'full' ? (
            <div className="text-sm text-blue-800 space-y-2">
              <p>
                <strong>Volledige Widget:</strong> Voor gebruik op de hoofdblog pagina. Bezoekers kunnen artikelen browsen en lezen op deze pagina.
              </p>
              <p>
                Artikelen worden geopend via hash routing (bijv. <code className="bg-blue-100 px-1 rounded">/blog#artikel/slug</code>).
              </p>
            </div>
          ) : (
            <div className="text-sm text-blue-800 space-y-2">
              <p>
                <strong>Compact Widget:</strong> Voor gebruik op andere pagina's (homepage, footer, sidebar).
              </p>
              <p>
                Bij klik navigeren bezoekers automatisch naar de hoofdblog pagina waar het volledige artikel wordt geopend.
              </p>
              <p className="text-xs bg-blue-100 rounded px-2 py-1 mt-2">
                💡 Zorg dat de gebruiker ook de <strong>Volledige Widget</strong> op hun blog pagina heeft geïnstalleerd.
              </p>
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">
            Admin Instructies
          </h3>
          <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
            <li>Verifieer handmatig of de widget correct werkt op de custom domain</li>
            <li>Test de widget URLs in een incognito venster</li>
            <li>Schakel "Gebruik Custom Domain" pas in nadat verificatie is voltooid</li>
            <li>Verstrek de embed code aan de gebruiker via een veilig kanaal</li>
            <li>Voor compact widget: controleer dat blog_page_url correct is ingesteld</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
